import * as React from "react";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import {
	getOpenDistributionsThunk,
	triggerManualJob,
} from "../../../redux/thunks/HomeThunks";
import { SaveAndClose } from "./SaveAndClose";
import { IDistributionState } from "../../../redux/types/IDistributionState";
import { getPoolsThunk } from "../../../redux/thunks/DataIngestionThunks";
import { IUsagePoolState } from "../../../redux/types/IUsagePoolState";
import DropdownDataInput from "../../../repertoire/components/dropdownDataInput/DropdownDataInput";

export interface IApplyCommissionsPostAllocationsProps {
	createNewJob: () => any;
	triggerManualJob: typeof triggerManualJob;
	manualJobParameters: ITriggerManualJob;
	formControls: any;
	getOpenDistributions: typeof getOpenDistributionsThunk;
	openDistributions?: IDistributionState[];
	getPools: typeof getPoolsThunk;
	pools?: IUsagePoolState[];
	scheduledJobSet?: (value: boolean) => void;
	onError?: (errorMessage: string) => void;
}

export const ApplyCommissionsPostAllocationsParameters = (
	props: IApplyCommissionsPostAllocationsProps,
) => {
	const [data, setData] = React.useState(props.formControls);
	const [generalError, setGeneralError] = React.useState<string[]>([]);

	React.useEffect(() => {
		const { getOpenDistributions, openDistributions, getPools, pools } =
			props;

		if (openDistributions?.length === 0) getOpenDistributions();
		if (pools?.length === 0) getPools();
	}, []);

	const handleTriggerManualJob = async () => {
		const errors = validateFields();

		if (errors.length === 0) {
			await props.triggerManualJob(data);
			return true;
		}

		return false;
	};

	const getOptions = (formItem) => {
		let optionsList = [];

		switch (formItem.ControlID) {
			case "ALKCOM_DoUndoAction":
				optionsList = formItem.Options;
				break;
			case "ALKCOM_Distribution":
				optionsList =
					props.openDistributions.map((e) => {
						return {
							code: e.distributionID,
							description: `${e.distributionCode} - ${e.description}`,
						};
					}) || [];
				break;
			case "ALKCOM_Pool":
				const selectedDistributionId = data.find(
					(item) => item.ControlID === "ALKCOM_Distribution",
				)?.CurrentValue;
				const filteredPools =
					props.pools.filter((p) =>
						props.openDistributions
							?.find(
								(d) =>
									d.distributionID === selectedDistributionId,
							)
							?.distributionPools?.some(
								(dp) => dp.poolID === p.poolID,
							),
					) || [];

				optionsList = filteredPools.map((pool) => ({
					code: pool.poolID,
					description: `${pool.poolCode} - ${pool.description}`,
				}));

				if (optionsList.length === 0) {
					optionsList = [
						{
							code: "",
							description:
								"No pools available for selected distribution",
						},
					];
				}
				break;
		}

		return optionsList;
	};

	const validateFields = () => {
		let errors: string[] = [];

		data.forEach((item) => {
			if (
				(item.CurrentValue === null ||
					item.CurrentValue === undefined ||
					item.CurrentValue === "") &&
				(item.Readonly === false || item.ControlID === "ALKCOM_Pool")
			) {
				errors.push(`${item.Caption} field is required`);
			}
		});

		setGeneralError(errors);
		return errors;
	};

	const handleChange = (formItem: any, value?: any) => {
		setData((prevState) => {
			return prevState.map((item) => {
				if (
					formItem.ControlID === "ALKCOM_DoUndoAction" &&
					item.ControlID === "ALKCOM_CommissionPercentage"
				) {
					return {
						...item,
						Readonly: value !== "Do",
						CurrentValue: "0.00",
					};
				} else if (
					formItem.ControlID === "ALKCOM_Distribution" &&
					item.ControlID === "ALKCOM_Pool"
				) {
					return {
						...item,
						Readonly:
							value === "" ||
							value === null ||
							value === "undefined",
						CurrentValue: null,
					};
				} else if (item.ControlID === formItem.ControlID) {
					return {
						...item,
						CurrentValue: value,
					};
				}
				return item;
			});
		});
	};

	const getFormComponents = (jobControls) => {
		return jobControls.map((formItem) => {
			if (formItem.Type === "combobox") {
				var optionList = getOptions(formItem);
				return (
					<div
						className="form-group col-md-6 col-sm-6 col-xs-12"
						key={formItem.ControlID}
					>
						<DropdownDataInput
							label={formItem.Caption}
							fieldName={formItem.ControlID}
							changeData={(value) => {
								handleChange(formItem, value);
							}}
							value={formItem.CurrentValue}
							options={optionList}
							allowNull={false}
							isHidden={false}
							isMandatory
							readOnly={formItem.Readonly}
						/>
					</div>
				);
			} else if (formItem.Type === "text-input") {
				return (
					<div
						className="form-group col-md-6 col-sm-6 col-xs-12"
						key={formItem.ControlID}
					>
						<div className="dropdownDataInput">
							<TextDataInput
								label={formItem.Caption}
								fieldName={formItem.Caption}
								changeData={(text) =>
									handleChange(formItem, text)
								}
								fieldType="integer"
								value={formItem.CurrentValue as string}
								isHidden={false}
								isMandatory={true}
								readOnly={formItem.Readonly}
							/>
						</div>
					</div>
				);
			}
		});
	};

	return (
		<div className="form-group col-md-12 col-sm-12 col-xs-12">
			<div className="alert alert-warning alert-dismissible">
				<span>
					{
						"Please ensure that you have ran the Undo ALKCOM job for the selected parameters if required"
					}
				</span>
			</div>
			{generalError.length > 0 && (
				<div className="alert alert-danger alert-dismissible">
					<span>{generalError.join(", ")}</span>
					<button
						type="button"
						className="close"
						aria-label="Close"
						onClick={() => setGeneralError([])}
					>
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
			)}
			<div className="row">{data && getFormComponents(data)}</div>
			<div className="row">
				<SaveAndClose
					manualJobParameters={props.manualJobParameters}
					triggerManualJob={handleTriggerManualJob}
					createNewJob={props.createNewJob}
					scheduledJobSet={props.scheduledJobSet}
					onError={props.onError}
				/>
			</div>
		</div>
	);
};
